import { Loader2, Pencil, Trash2 } from "lucide-react"
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import type { LibraryItem } from "@/components/library/library-item"
import { LibraryItems, LibraryItemsReporter, type LibraryItemActionSlot } from "@/components/library/library-items"
import { LibraryNodeEmpty, LibraryNodeError, LibraryNodeLoading } from "@/components/library/library-node-state"
import { Button } from "@/components/ui/primitives"
import { EntityNameEditDialog } from "@/components/workspace/entity-name-edit-dialog"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { useTranslation } from "@/hooks/use-translation"
import {
  deleteProjectLibraryUpload,
  fetchLibraryUploads,
  type LibraryUploadCursor,
  type LibraryUploadItem,
  renameProjectLibraryUpload,
} from "@/lib/library-api"
import { projectLibraryUploadSource } from "@/lib/library-source"
import type { LibraryViewMode } from "@/lib/library-view-mode"
import { cn } from "@/lib/utils"

type LibraryUploadsNodeProps = {
  projectId: number
  mode: LibraryViewMode
  /** Растёт после каждой загрузки со страницы — заставляет ленту перечитаться с начала. */
  reloadToken?: number
  /** Без прав на изменение контента переименование и удаление не показываем. */
  canEdit?: boolean
  focusedKey?: string | null
  onFocusItem?: (key: string) => void
  onOpenItem?: (item: LibraryItem) => void
  onItemsChange?: (items: LibraryItem[]) => void
  /** Состав секции изменился — счётчик в дереве надо перечитать. */
  onCountChange?: () => void
}

const NO_ITEMS: LibraryItem[] = []

/** Совпадает с ProjectLibraryUpload::MAX_NAME_LENGTH — сервер обрезает молча. */
const UPLOAD_NAME_MAX_LENGTH = 200

function uploadToLibraryItem(upload: LibraryUploadItem): LibraryItem {
  const isVideo = upload.media_type === "video"
  return {
    key: `upload-${upload.id}`,
    kind: upload.media_type,
    label: upload.label,
    createdAt: upload.created_at,
    // thumb_url приходит только у изображения; у видео и аудио превью нет, и ставить
    // сюда сам файл нельзя — он уехал бы в <img src>.
    thumbUrl: upload.thumb_url,
    source: projectLibraryUploadSource(upload.blob_id),
    asset: {
      title: upload.label,
      kind: upload.media_type,
      // Просмотрщик берёт аудио и картинку из url, а видео — из videoUrl (ему нужен
      // same-origin поток для чтения кадров); третьего поля у LibraryAsset нет.
      url: isVideo ? null : upload.url,
      videoUrl: isVideo ? upload.url : null,
      downloadUrl: upload.download_url,
      createdAt: upload.created_at,
    },
  }
}

/** Иконка-действие над элементом: та же подача, что у глазка просмотра в режиме выбора. */
function ItemActionButton({
  label,
  danger = false,
  inline = false,
  onClick,
  children,
}: {
  label: string
  danger?: boolean
  /** Слот со сплошным фоном (подпись плитки, строка списка): чип там выглядел бы заплаткой. */
  inline?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      data-library-item-action=""
      tabIndex={-1}
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={cn(
        "rounded p-1 opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100",
        // Подпись плитки — строка в 28px: чип с полным паддингом её распирал бы.
        inline
          ? cn(
              "p-0.5 text-ui-text-muted hover:bg-ui-surface-raised",
              danger ? "hover:text-ui-danger" : "hover:text-ui-text-primary",
            )
          : cn("bg-[var(--ui-chip-bg-strong)] backdrop-blur-sm", danger ? "text-ui-danger" : "text-ui-text-primary"),
      )}
    >
      {children}
    </button>
  )
}

/**
 * Секция «Загрузки»: файлы, залитые пользователем на проект. В отличие от «Эскизов»
 * лента листается курсором — секция пополняется руками и растёт без потолка.
 */
export function LibraryUploadsNode({
  projectId,
  mode,
  reloadToken = 0,
  canEdit = false,
  focusedKey,
  onFocusItem,
  onOpenItem,
  onItemsChange,
  onCountChange,
}: LibraryUploadsNodeProps) {
  const { t } = useTranslation(["workspace", "common"])
  const [uploads, setUploads] = useState<LibraryUploadItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [renaming, setRenaming] = useState<LibraryUploadItem | null>(null)
  const [removing, setRemoving] = useState<LibraryUploadItem | null>(null)
  const [removeBusy, setRemoveBusy] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)
  // Поколение ленты: растёт на каждый сброс (смена проекта, ретрай, новая пачка). Ответ
  // loadMore из прошлого поколения выбрасываем — иначе он дописывал бы устаревшую страницу
  // в уже перечитанный список и подменял курсор, оставляя в ленте недолистываемую дыру.
  const generationRef = useRef(0)
  const pageRef = useRef({
    generation: 0,
    cursor: null as LibraryUploadCursor | null,
    hasMore: false,
    loadingMore: false,
  })
  // Сентинел держим состоянием, а не ref: смена режима просмотра пересоздаёт разметку,
  // а object-ref не перезапускает эффект — наблюдатель остался бы на отсоединённом узле.
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    const generation = generationRef.current + 1
    generationRef.current = generation
    setUploads([])
    setLoading(true)
    setLoadFailed(false)
    setHasMore(false)
    pageRef.current = { generation, cursor: null, hasMore: false, loadingMore: false }
    fetchLibraryUploads(projectId, { signal: controller.signal })
      .then((page) => {
        setUploads(page.uploads)
        setHasMore(page.has_more)
        pageRef.current = { generation, cursor: page.next_cursor, hasMore: page.has_more, loadingMore: false }
      })
      .catch(() => {
        if (!controller.signal.aborted) setLoadFailed(true)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [projectId, reloadKey, reloadToken])

  const loadMore = useCallback(async () => {
    const { generation, cursor, hasMore: canLoad, loadingMore: inFlight } = pageRef.current
    if (inFlight || !canLoad || cursor == null) return
    pageRef.current = { ...pageRef.current, loadingMore: true }
    setLoadingMore(true)
    try {
      const page = await fetchLibraryUploads(projectId, { before: cursor })
      // Лента успела перечитаться, пока страница ехала: её содержимое уже неактуально.
      if (generationRef.current !== generation) return
      setUploads((prev) => {
        const known = new Set(prev.map((u) => u.id))
        return [...prev, ...page.uploads.filter((u) => !known.has(u.id))]
      })
      setHasMore(page.has_more)
      pageRef.current = { generation, cursor: page.next_cursor, hasMore: page.has_more, loadingMore: false }
    } catch {
      if (generationRef.current === generation) pageRef.current = { ...pageRef.current, loadingMore: false }
    } finally {
      if (generationRef.current === generation) setLoadingMore(false)
    }
  }, [projectId])

  const loadMoreRef = useRef(loadMore)
  loadMoreRef.current = loadMore

  useEffect(() => {
    if (!sentinel || !hasMore || loading) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void loadMoreRef.current()
      },
      { rootMargin: "400px 0px" },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
    // uploads.length в зависимостях обязателен: колбэк наблюдателя срабатывает на СМЕНУ
    // пересечения, а страница могла не сдвинуть сентинел с экрана — без нового события
    // догрузка встала бы.
  }, [sentinel, hasMore, loading, uploads.length])

  // Ошибку наружу не гасим: диалог ловит её сам и оставляет форму открытой с текстом.
  const submitRename = useCallback(
    async (name: string) => {
      if (!renaming) return
      const updated = await renameProjectLibraryUpload(projectId, renaming.id, name)
      setUploads((prev) => prev.map((u) => (u.id === updated.id ? updated : u)))
    },
    [projectId, renaming],
  )

  const submitRemove = useCallback(async () => {
    if (!removing) return
    setRemoveBusy(true)
    setRemoveError(null)
    try {
      await deleteProjectLibraryUpload(projectId, removing.id)
      // Плитку убираем на месте: перечитывать ленту с первой страницы ради одной строки
      // значило бы отбросить пользователя к началу и потерять позицию прокрутки.
      setUploads((prev) => prev.filter((u) => u.id !== removing.id))
      setRemoving(null)
      onCountChange?.()
    } catch (e) {
      // Ошибку показываем там же, где действие: диалог остаётся открытым, плитка на месте.
      setRemoveError(e instanceof Error ? e.message : t("library.uploads.deleteFailed"))
    } finally {
      setRemoveBusy(false)
    }
  }, [projectId, removing, onCountChange, t])

  const items = uploads.map(uploadToLibraryItem)

  const itemActions = useCallback(
    (item: LibraryItem, slot: LibraryItemActionSlot) => {
      const upload = uploads.find((u) => `upload-${u.id}` === item.key)
      if (!upload) return null
      const rename = (
        <ItemActionButton key="rename" label={t("library.uploads.rename")} inline onClick={() => setRenaming(upload)}>
          <Pencil className="h-3.5 w-3.5" />
        </ItemActionButton>
      )
      const remove = (
        <ItemActionButton
          key="delete"
          label={t("library.uploads.delete")}
          danger
          inline={slot === "row"}
          onClick={() => setRemoving(upload)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </ItemActionButton>
      )
      // Карандаш — на подпись, корзина — в угол превью: поверх кадра карандаш означает
      // правку изображения (эскиз), и рядом с именем файла он не спорит с этим смыслом.
      if (slot === "caption") return rename
      if (slot === "media") return remove
      return (
        <>
          {rename}
          {remove}
        </>
      )
    },
    [uploads, t],
  )

  const dialogs = (
    <>
      {/* Форма имени — общий диалог воркспейса: поля, отступы и разбор ошибки у него уже свои. */}
      <EntityNameEditDialog
        open={renaming != null}
        onOpenChange={(open) => {
          if (!open) setRenaming(null)
        }}
        title={t("library.uploads.rename")}
        nameLabel={t("library.uploads.renameNameLabel")}
        initialName={renaming?.label ?? ""}
        maxLength={UPLOAD_NAME_MAX_LENGTH}
        onSubmit={submitRename}
        closeAriaLabel={t("common:close")}
        cancelLabel={t("common:cancel")}
        saveLabel={t("common:save")}
      />

      <WorkspaceDialog
        open={removing != null}
        onOpenChange={(open) => {
          if (!open) {
            setRemoving(null)
            setRemoveError(null)
          }
        }}
        title={t("library.uploads.delete")}
        closeAriaLabel={t("common:close")}
        size="sm"
        bodyClassName="space-y-3 p-5 pt-4"
        footer={
          <>
            <Button type="button" size="sm" variant="secondary" onClick={() => setRemoving(null)} disabled={removeBusy}>
              {t("common:cancel")}
            </Button>
            <Button type="button" size="sm" variant="danger" disabled={removeBusy} onClick={submitRemove}>
              {removeBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : t("library.uploads.deleteConfirm")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ui-text-secondary">
          {t("library.uploads.deleteWarning", { name: removing?.label ?? "" })}
        </p>
        {removeError ? (
          <div className="rounded-lg bg-rose-500/10 p-2 text-xs text-ui-status-failed">{removeError}</div>
        ) : null}
      </WorkspaceDialog>
    </>
  )

  // Диалоги стоят СНАРУЖИ веток содержимого: внутри ветки items.length > 0 удаление
  // последней плитки размонтировало бы диалог прямо во время его же запроса.
  let body: ReactNode
  if (loading || loadFailed) {
    body = (
      <>
        <LibraryItemsReporter items={NO_ITEMS} onItemsChange={onItemsChange} />
        {loading ? <LibraryNodeLoading /> : <LibraryNodeError onRetry={() => setReloadKey((k) => k + 1)} />}
      </>
    )
  } else if (items.length === 0) {
    body = (
      <>
        <LibraryItemsReporter items={NO_ITEMS} onItemsChange={onItemsChange} />
        {/* Про перетаскивание и кнопку говорим только там, где они есть: в модалке выбора
            секция read-only, и подсказка «перетащите файлы сюда» вела бы в никуда. */}
        <LibraryNodeEmpty>{canEdit ? t("library.uploads.empty") : t("library.uploads.emptyReadOnly")}</LibraryNodeEmpty>
      </>
    )
  } else {
    body = (
      <>
        <LibraryItemsReporter items={items} onItemsChange={onItemsChange} />
        <LibraryItems
          items={items}
          mode={mode}
          listLayout="table"
          aspectClass="aspect-square"
          focusedKey={focusedKey}
          onFocusItem={onFocusItem}
          onOpenItem={onOpenItem}
          itemActions={canEdit ? itemActions : undefined}
        />
        {hasMore ? (
          <div ref={setSentinel} className="flex justify-center py-4">
            {loadingMore ? <Loader2 className="h-4 w-4 animate-spin text-ui-text-muted" /> : null}
          </div>
        ) : null}
      </>
    )
  }

  return (
    <>
      {body}
      {dialogs}
    </>
  )
}
